import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { Sidebar, Text, Heading, Button, Input, Select, LoadingSpinner, SortableTableHeader } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { supabase } from '../lib/supabase'
import { XMarkIcon } from '@heroicons/react/24/outline'


interface WppTicketHeader {
  agent: string | null
  protocol: string
  phone: string | null
  start_datetime: string | null
  end_datetime: string | null
  service_duration: number | null
  status: string | null
  rating: number | null
  classification: string | null
}

interface WppTicketMessage {
  id: string
  datetime: string
  protocol: string | null
  message: string | null
  sender: string | null
  type: string | null
}

type SortKey = 'agent' | 'protocol' | 'phone' | 'start_datetime' | 'end_datetime' | 'status' | 'rating' | 'classification'

const Whatsapp: React.FC = () => {
  // Função para calcular datas do último mês
  const getLastMonthDates = () => {
    const hoje = new Date()
    const fim = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate())
    const inicio = new Date(hoje.getFullYear(), hoje.getMonth() - 1, hoje.getDate())
    
    const formatDate = (date: Date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
    
    return {
      inicio: formatDate(inicio),
      fim: formatDate(fim)
    }
  }

  const lastMonthDates = getLastMonthDates()

  const [tickets, setTickets] = useState<WppTicketHeader[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasInitialLoad, setHasInitialLoad] = useState(false)

  // Filtros - inicializados com último mês
  const [filtroDataInicio, setFiltroDataInicio] = useState(lastMonthDates.inicio)
  const [filtroDataFim, setFiltroDataFim] = useState(lastMonthDates.fim)
  const [filtroAgente, setFiltroAgente] = useState('')
  const [filtroStatus, setFiltroStatus] = useState('')
  const [filtroRating, setFiltroRating] = useState('')
  const [filtroClassificacao, setFiltroClassificacao] = useState('')
  const [filtroTelefone, setFiltroTelefone] = useState('')
  const [filtroProtocolo, setFiltroProtocolo] = useState('')

  // Distintos para selects
  const [agentes, setAgentes] = useState<string[]>([])
  const [statuses, setStatuses] = useState<string[]>([])
  const [classificacoes, setClassificacoes] = useState<string[]>([])

  // Ordenação e paginação
  const [sortBy, setSortBy] = useState<SortKey>('start_datetime')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)

  // Abas
  const [activeTab, setActiveTab] = useState<'atendimentos' | 'dashboard'>('atendimentos')

  // Detalhes (diálogo)
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [selectedHeader, setSelectedHeader] = useState<WppTicketHeader | null>(null)
  const [dialogLoading, setDialogLoading] = useState(false)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [dialogItems, setDialogItems] = useState<WppTicketMessage[]>([])

  const formatDateTime = (iso: string | null) => {
    if (!iso) return '-'
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '-'
    return d.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    })
  }

  const formatDuration = (msOrSec: number | null) => {
    if (msOrSec === null || msOrSec === undefined) return '-'
    // A tabela declara bigint; não está claro se em milissegundos ou segundos. Assumiremos segundos.
    const totalSec = Number(msOrSec)
    if (!Number.isFinite(totalSec)) return '-'
    const h = Math.floor(totalSec / 3600)
    const m = Math.floor((totalSec % 3600) / 60)
    const s = totalSec % 60
    const pad = (n: number) => n.toString().padStart(2, '0')
    return `${pad(h)}:${pad(m)}:${pad(s)}`
  }

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key)
      setSortOrder('asc')
    }
  }

  // Função para buscar tickets com filtros no servidor
  const fetchTickets = useCallback(async () => {
    // Só busca se houver filtros de data aplicados
    if (!filtroDataInicio || !filtroDataFim) {
      setTickets([])
      setAgentes([])
      setStatuses([])
      setClassificacoes([])
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Construir query base com filtros
      let baseQuery = supabase
        .from('wpp_ticket_list')
        .select('*')

      // Sempre aplicar filtros de data (obrigatórios)
      baseQuery = baseQuery.gte('start_datetime', new Date(filtroDataInicio + 'T00:00:00').toISOString())
      baseQuery = baseQuery.lte('start_datetime', new Date(filtroDataFim + 'T23:59:59').toISOString())

      if (filtroAgente) {
        baseQuery = baseQuery.eq('agent', filtroAgente)
      }
      if (filtroStatus) {
        baseQuery = baseQuery.eq('status', filtroStatus)
      }
      if (filtroClassificacao) {
        baseQuery = baseQuery.eq('classification', filtroClassificacao)
      }
      if (filtroRating) {
        const num = Number(filtroRating)
        if (!Number.isNaN(num)) {
          baseQuery = baseQuery.eq('rating', num)
        }
      }
      if (filtroTelefone) {
        baseQuery = baseQuery.ilike('phone', `%${filtroTelefone}%`)
      }
      if (filtroProtocolo) {
        baseQuery = baseQuery.ilike('protocol', `%${filtroProtocolo}%`)
      }

      baseQuery = baseQuery.order('start_datetime', { ascending: false })

      // Buscar todos os registros usando paginação
      const allData: WppTicketHeader[] = []
      const batchSize = 1000
      let from = 0
      let hasMore = true

      while (hasMore) {
        const query = baseQuery.range(from, from + batchSize - 1)
        const { data, error } = await query
        
        if (error) throw error

        if (data && data.length > 0) {
          allData.push(...(data as WppTicketHeader[]))
          
          // Se retornou menos que o batchSize, não há mais dados
          if (data.length < batchSize) {
            hasMore = false
          } else {
            from += batchSize
          }
        } else {
          hasMore = false
        }
      }

      setTickets(allData)

      // Distintos para selects
      const setFromDistinct = (arr: (string | null)[]) => Array.from(new Set(arr.filter(Boolean) as string[])).sort((a, b) => a.localeCompare(b))
      setAgentes(setFromDistinct(allData.map(r => r.agent)))
      setStatuses(setFromDistinct(allData.map(r => r.status)))
      setClassificacoes(setFromDistinct(allData.map(r => r.classification)))
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar tickets')
    } finally {
      setLoading(false)
    }
  }, [filtroDataInicio, filtroDataFim, filtroAgente, filtroStatus, filtroRating, filtroClassificacao, filtroTelefone, filtroProtocolo])

  // Buscar automaticamente apenas na primeira carga (quando os filtros já estão inicializados)
  useEffect(() => {
    if (!hasInitialLoad && filtroDataInicio && filtroDataFim) {
      setHasInitialLoad(true)
      fetchTickets()
    }
  }, [hasInitialLoad, filtroDataInicio, filtroDataFim, fetchTickets])

  const filteredAndSorted = useMemo(() => {
    const copy = [...tickets]
    return copy.sort((a, b) => {
      let aValue: any = (a as any)[sortBy]
      let bValue: any = (b as any)[sortBy]

      if (sortBy === 'start_datetime' || sortBy === 'end_datetime') {
        aValue = aValue ? new Date(aValue).getTime() : -Infinity
        bValue = bValue ? new Date(bValue).getTime() : -Infinity
      } else if (sortBy === 'rating') {
        aValue = aValue ?? -Infinity
        bValue = bValue ?? -Infinity
      } else {
        aValue = (aValue ?? '').toString().toLowerCase()
        bValue = (bValue ?? '').toString().toLowerCase()
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [tickets, sortBy, sortOrder])

  useEffect(() => {
    setCurrentPage(1)
  }, [sortBy, sortOrder])

  const totalPages = Math.ceil(filteredAndSorted.length / itemsPerPage) || 1
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const pageRows = filteredAndSorted.slice(startIndex, endIndex)

  // Indicadores para Dashboard
  const dashboardMetrics = useMemo(() => {
    const total = tickets.length
    const ratings = tickets.map(t => t.rating).filter((r): r is number => r !== null && Number.isFinite(r))
    const mediaRating = ratings.length > 0 ? ratings.reduce((sum, n) => sum + n, 0) / ratings.length : null
    
    const duracoes = tickets
      .map(t => t.service_duration)
      .filter((d): d is number => d !== null && Number.isFinite(d))
    const tempoMedio = duracoes.length > 0 
      ? Math.round(duracoes.reduce((acc, n) => acc + n, 0) / duracoes.length)
      : null

    // Distribuição por status
    const statusCounts = new Map<string, number>()
    tickets.forEach(t => {
      const status = t.status || 'Sem status'
      statusCounts.set(status, (statusCounts.get(status) || 0) + 1)
    })

    // Distribuição por agente
    const agentCounts = new Map<string, number>()
    tickets.forEach(t => {
      const agent = t.agent || 'Sem agente'
      agentCounts.set(agent, (agentCounts.get(agent) || 0) + 1)
    })

    // Distribuição por rating
    const ratingCounts = new Map<number, number>()
    ratings.forEach(r => {
      ratingCounts.set(r, (ratingCounts.get(r) || 0) + 1)
    })

    return {
      total,
      mediaRating,
      tempoMedio,
      statusCounts: Array.from(statusCounts.entries()).sort((a, b) => b[1] - a[1]),
      agentCounts: Array.from(agentCounts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 10),
      ratingCounts: Array.from(ratingCounts.entries()).sort((a, b) => a[0] - b[0])
    }
  }, [tickets])


  const openDetails = async (header: WppTicketHeader) => {
    setSelectedHeader(header)
    setIsDetailsOpen(true)
    setDialogLoading(true)
    setDialogError(null)
    setDialogItems([])

    try {
      const { data, error } = await supabase
        .from('wpp_ticket_detail')
        .select('*')
        .eq('protocol', header.protocol)
        .order('datetime', { ascending: true })

      if (error) throw error
      setDialogItems((data as WppTicketMessage[]) || [])
    } catch (err: any) {
      setDialogError(err.message || 'Erro ao carregar diálogo')
    } finally {
      setDialogLoading(false)
    }
  }

  const closeDetails = () => {
    setIsDetailsOpen(false)
    setSelectedHeader(null)
    setDialogItems([])
    setDialogError(null)
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
          <PermissionGuard 
            moduleName="atendimentos_whatsapp" 
            permissionType="show"
            fallback={
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                    Acesso Negado
                  </Text>
                  <Text size="sm" color="muted">
                    Você não tem permissão para acessar esta página.
                  </Text>
                </div>
              </div>
            }
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div>
                    <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                      Whatsapp
                    </Heading>
                    <Text size="sm" color="muted" className="mt-1">Atendimentos no Whatsapp - HiPlatform</Text>
                  </div>
                </div>
              </div>

              {/* Filtros - padrão SilvIA */}
              <div className="px-6 py-4">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                  <div className="mb-4">
                    <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                      Filtros
                    </Heading>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data Inicial</Text>
                      <Input type="date" value={filtroDataInicio} onChange={(e) => setFiltroDataInicio(e.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data Final</Text>
                      <Input type="date" value={filtroDataFim} onChange={(e) => setFiltroDataFim(e.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Agente</Text>
                      <Select value={filtroAgente} onChange={(e) => setFiltroAgente(e.target.value)}>
                        <option value="">Todos</option>
                        {agentes.map(a => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Status</Text>
                      <Select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
                        <option value="">Todos</option>
                        {statuses.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Rating</Text>
                      <Select value={filtroRating} onChange={(e) => setFiltroRating(e.target.value)}>
                        <option value="">Todos</option>
                        {[1,2,3,4,5].map(r => (
                          <option key={r} value={String(r)}>{r}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Classificação</Text>
                      <Select value={filtroClassificacao} onChange={(e) => setFiltroClassificacao(e.target.value)}>
                        <option value="">Todas</option>
                        {classificacoes.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Telefone</Text>
                      <Input type="text" placeholder="Buscar..." value={filtroTelefone} onChange={(e) => setFiltroTelefone(e.target.value)} />
                    </div>
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Protocolo</Text>
                      <Input type="text" placeholder="Buscar..." value={filtroProtocolo} onChange={(e) => setFiltroProtocolo(e.target.value)} />
                    </div>
                    <div className="md:col-span-2 flex flex-nowrap items-end justify-start gap-2">
                      <Button
                        variant="solid"
                        size="sm"
                        className="shrink-0"
                        onClick={() => {
                          if (filtroDataInicio && filtroDataFim) {
                            fetchTickets()
                          }
                        }}
                        disabled={!filtroDataInicio || !filtroDataFim || loading}
                      >
                        {loading ? 'Buscando...' : 'Buscar'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={() => {
                          const dates = getLastMonthDates()
                          setFiltroDataInicio(dates.inicio)
                          setFiltroDataFim(dates.fim)
                          setFiltroAgente('')
                          setFiltroStatus('')
                          setFiltroRating('')
                          setFiltroClassificacao('')
                          setFiltroTelefone('')
                          setFiltroProtocolo('')
                        }}
                      >
                        Limpar Filtros
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Abas */}
              <div className="px-6 pt-2">
                <div className="border-b border-gray-200 dark:border-gray-700">
                  <nav className="-mb-px flex space-x-8">
                    <button
                      onClick={() => setActiveTab('atendimentos')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'atendimentos'
                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                    >
                      Atendimentos
                    </button>
                    <button
                      onClick={() => setActiveTab('dashboard')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'dashboard'
                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                    >
                      Dashboard
                    </button>
                  </nav>
                </div>
              </div>

              {activeTab === 'atendimentos' && (
                <>
                  {/* Lista */}
                  <div className="flex-1 p-6 pt-0">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden h-full flex flex-col">
                  {loading ? (
                    <div className="flex-1 flex items-center justify-center">
                      <LoadingSpinner size="lg" className="mx-auto" />
                    </div>
                  ) : error ? (
                    <div className="flex-1 flex items-center justify-center">
                      <Text size="lg" className="text-red-600 dark:text-red-400">{error}</Text>
                    </div>
                  ) : (
                    <div className="px-4 py-5 sm:p-6 flex-1 flex flex-col">
                      <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">Atendimentos</Heading>
                      <div className="flex-1 overflow-auto">
                        <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                            <tr>
                              <SortableTableHeader sortKey="agent" currentSort={sortBy} sortOrder={sortOrder} onSort={k => toggleSort(k as SortKey)} className="text-left">
                                Agente
                              </SortableTableHeader>
                              <SortableTableHeader sortKey="protocol" currentSort={sortBy} sortOrder={sortOrder} onSort={k => toggleSort(k as SortKey)} className="text-left">
                                Protocolo
                              </SortableTableHeader>
                              <SortableTableHeader sortKey="phone" currentSort={sortBy} sortOrder={sortOrder} onSort={k => toggleSort(k as SortKey)} className="text-left">
                                Telefone
                              </SortableTableHeader>
                              <SortableTableHeader sortKey="start_datetime" currentSort={sortBy} sortOrder={sortOrder} onSort={k => toggleSort(k as SortKey)} className="text-left">
                                Início
                              </SortableTableHeader>
                              <SortableTableHeader sortKey="end_datetime" currentSort={sortBy} sortOrder={sortOrder} onSort={k => toggleSort(k as SortKey)} className="text-left">
                                Fim
                              </SortableTableHeader>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Duração</th>
                              <SortableTableHeader sortKey="status" currentSort={sortBy} sortOrder={sortOrder} onSort={k => toggleSort(k as SortKey)} className="text-left">
                                Status
                              </SortableTableHeader>
                              <SortableTableHeader sortKey="rating" currentSort={sortBy} sortOrder={sortOrder} onSort={k => toggleSort(k as SortKey)} className="text-right">
                                Rating
                              </SortableTableHeader>
                              <SortableTableHeader sortKey="classification" currentSort={sortBy} sortOrder={sortOrder} onSort={k => toggleSort(k as SortKey)} className="text-left">
                                Classificação
                              </SortableTableHeader>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {pageRows.map((row) => (
                              <tr key={row.protocol} onClick={() => openDetails(row)} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                                <td className="px-4 py-4 text-left"><Text size="sm" className="text-gray-700 dark:text-gray-300">{row.agent || '-'}</Text></td>
                                <td className="px-4 py-4 text-left"><Text size="sm" className="text-gray-900 dark:text-white font-medium">{row.protocol}</Text></td>
                                <td className="px-4 py-4 text-left"><Text size="sm" className="text-gray-700 dark:text-gray-300">{row.phone || '-'}</Text></td>
                                <td className="px-4 py-4 text-left"><Text size="sm" className="text-gray-500 dark:text-gray-400">{formatDateTime(row.start_datetime)}</Text></td>
                                <td className="px-4 py-4 text-left"><Text size="sm" className="text-gray-500 dark:text-gray-400">{formatDateTime(row.end_datetime)}</Text></td>
                                <td className="px-4 py-4 text-left"><Text size="sm" className="text-gray-700 dark:text-gray-300">{formatDuration(row.service_duration)}</Text></td>
                                <td className="px-4 py-4 text-left"><Text size="sm" className="text-gray-700 dark:text-gray-300">{row.status || '-'}</Text></td>
                                <td className="px-4 py-4 text-right"><Text size="sm" className="text-gray-700 dark:text-gray-300">{row.rating ?? '-'}</Text></td>
                                <td className="px-4 py-4 text-left"><Text size="sm" className="text-gray-700 dark:text-gray-300">{row.classification || '-'}</Text></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {totalPages > 1 && (
                          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                                <Text size="sm">Mostrando {startIndex + 1} a {Math.min(endIndex, filteredAndSorted.length)} de {filteredAndSorted.length} registros</Text>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                  disabled={currentPage === 1}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                  Anterior
                                </button>
                                <div className="flex space-x-1">
                                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let pageNumber: number
                                    if (totalPages <= 5) {
                                      pageNumber = i + 1
                                    } else if (currentPage <= 3) {
                                      pageNumber = i + 1
                                    } else if (currentPage >= totalPages - 2) {
                                      pageNumber = totalPages - 4 + i
                                    } else {
                                      pageNumber = currentPage - 2 + i
                                    }
                                    return (
                                      <button
                                        key={pageNumber}
                                        onClick={() => setCurrentPage(pageNumber)}
                                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                                          currentPage === pageNumber
                                            ? 'bg-blue-600 text-white'
                                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                        }`}
                                      >
                                        {pageNumber}
                                      </button>
                                    )
                                  })}
                                </div>
                                <button
                                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                  disabled={currentPage === totalPages}
                                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                                >
                                  Próximo
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
                </>
              )}

              {activeTab === 'dashboard' && (
                <div className="flex-1 px-6 py-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                      <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-2">Total de Atendimentos</Text>
                      <Text size="3xl" className="text-gray-900 dark:text-white font-bold">{dashboardMetrics.total}</Text>
                    </div>
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                      <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-2">Média de Rating</Text>
                      <Text size="3xl" className="text-gray-900 dark:text-white font-bold">
                        {dashboardMetrics.mediaRating !== null ? dashboardMetrics.mediaRating.toFixed(1) : '—'}
                      </Text>
                    </div>
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                      <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-2">Tempo Médio de Duração</Text>
                      <Text size="3xl" className="text-gray-900 dark:text-white font-bold">
                        {dashboardMetrics.tempoMedio !== null ? formatDuration(dashboardMetrics.tempoMedio) : '—'}
                      </Text>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Distribuição por Status */}
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                      <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">Distribuição por Status</Heading>
                      <div className="space-y-2">
                        {dashboardMetrics.statusCounts.length > 0 ? (
                          dashboardMetrics.statusCounts.map(([status, count]) => (
                            <div key={status} className="flex items-center justify-between">
                              <Text size="sm" className="text-gray-700 dark:text-gray-300">{status}</Text>
                              <div className="flex items-center gap-2">
                                <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                  <div
                                    className="bg-blue-600 h-2 rounded-full"
                                    style={{ width: `${(count / dashboardMetrics.total) * 100}%` }}
                                  />
                                </div>
                                <Text size="sm" className="text-gray-900 dark:text-white font-medium w-12 text-right">
                                  {count}
                                </Text>
                              </div>
                            </div>
                          ))
                        ) : (
                          <Text size="sm" className="text-gray-500 dark:text-gray-400">Sem dados</Text>
                        )}
                      </div>
                    </div>

                    {/* Top 10 Agentes */}
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                      <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">Top 10 Agentes</Heading>
                      <div className="space-y-2">
                        {dashboardMetrics.agentCounts.length > 0 ? (
                          dashboardMetrics.agentCounts.map(([agent, count]) => (
                            <div key={agent} className="flex items-center justify-between">
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 truncate flex-1">{agent}</Text>
                              <div className="flex items-center gap-2 ml-2">
                                <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                  <div
                                    className="bg-green-600 h-2 rounded-full"
                                    style={{ width: `${(count / dashboardMetrics.total) * 100}%` }}
                                  />
                                </div>
                                <Text size="sm" className="text-gray-900 dark:text-white font-medium w-12 text-right">
                                  {count}
                                </Text>
                              </div>
                            </div>
                          ))
                        ) : (
                          <Text size="sm" className="text-gray-500 dark:text-gray-400">Sem dados</Text>
                        )}
                      </div>
                    </div>

                    {/* Distribuição por Rating */}
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 lg:col-span-2">
                      <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">Distribuição por Rating</Heading>
                      <div className="grid grid-cols-5 gap-4">
                        {[1, 2, 3, 4, 5].map((rating) => {
                          const count = dashboardMetrics.ratingCounts.find(([r]) => r === rating)?.[1] || 0
                          const percentage = dashboardMetrics.total > 0 ? (count / dashboardMetrics.total) * 100 : 0
                          return (
                            <div key={rating} className="text-center">
                              <Text size="2xl" className="text-gray-900 dark:text-white font-bold mb-1">{count}</Text>
                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 mb-1">
                                <div
                                  className={`h-4 rounded-full ${
                                    rating >= 4 ? 'bg-green-600' : rating >= 3 ? 'bg-yellow-600' : 'bg-red-600'
                                  }`}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                              <Text size="sm" className="text-gray-600 dark:text-gray-400">⭐ {rating}</Text>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Modal de detalhes */}
              {isDetailsOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                  <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeDetails}></div>
                    <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-5xl sm:p-6 max-h-[90vh] flex flex-col w-full">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Diálogo</Heading>
                          {selectedHeader && (
                            <Text size="sm" className="text-gray-600 dark:text-gray-300 mt-1">
                              Protocolo {selectedHeader.protocol} • {selectedHeader.phone || '-'} • {selectedHeader.agent || '-'}
                            </Text>
                          )}
                        </div>
                        <button onClick={closeDetails} className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none">
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </div>

                      <div className="flex-1 overflow-auto">
                        {dialogLoading ? (
                          <div className="flex items-center justify-center py-12">
                            <LoadingSpinner size="lg" />
                          </div>
                        ) : dialogError ? (
                          <div className="py-4">
                            <Text size="sm" className="text-red-600 dark:text-red-400">{dialogError}</Text>
                          </div>
                        ) : dialogItems.length === 0 ? (
                          <div className="py-4">
                            <Text size="sm" className="text-gray-600 dark:text-gray-300">Sem mensagens para este protocolo.</Text>
                          </div>
                        ) : (
                          <div className="px-1">
                            {dialogItems.map((msg) => {
                              const isAgent = msg.type === 'sent'
                              return (
                                <div key={msg.id} className={`flex ${isAgent ? 'justify-end' : 'justify-start'} mb-2`}>
                                  <div className={`${isAgent ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'} rounded-lg px-3 py-2 max-w-[70%] shadow-sm`}>
                                    <div className="text-[11px] opacity-75 mb-1">
                                      {msg.sender || (isAgent ? 'Agente' : 'Contato')}
                                    </div>
                                    <div className="whitespace-pre-wrap text-sm leading-relaxed break-words">{msg.message || ''}</div>
                                    <div className={`text-[10px] mt-1 ${isAgent ? 'text-white/80' : 'text-gray-500 dark:text-gray-300/80'}`}>{formatDateTime(msg.datetime)}</div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </PermissionGuard>
        </main>
      </div>
    </div>
  )
}

export default Whatsapp


